import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getRecentBlogPosts } from '../services/blogService';
import type { BlogPost as BlogPostType } from '../lib/supabase';

const CalendarIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const ClockIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const ArrowRightIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
);

interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    date: string;
    readTime: string;
    category: string;
    image: string;
}

const blogPosts: BlogPost[] = [
    {
        id: 1,
        title: "The Ultimate Guide to Choosing a Laptop in 2025",
        excerpt: "Navigate the complex world of laptop specs with our comprehensive guide. Learn what matters most for your specific needs and budget.",
        date: "Jan 15, 2025",
        readTime: "8 min read",
        category: "Buying Guide",
        image: "https://images.pexels.com/photos/7974/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 2,
        title: "Gaming Laptops vs Desktop PCs: The 2025 Showdown",
        excerpt: "Explore the pros and cons of gaming laptops versus desktop setups. Discover which option delivers the best value for gamers in 2025.",
        date: "Jan 12, 2025",
        readTime: "10 min read",
        category: "Gaming",
        image: "https://images.pexels.com/photos/1229861/pexels-photo-1229861.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 3,
        title: "AI-Powered Laptop Recommendations: How It Works",
        excerpt: "Take a behind-the-scenes look at how our AI analyzes millions of data points to deliver personalized laptop recommendations.",
        date: "Jan 8, 2025",
        readTime: "6 min read",
        category: "Technology",
        image: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 4,
        title: "Best Laptops for Students: Budget-Friendly Picks",
        excerpt: "Discover the top laptop options for students that balance performance, portability, and affordability without compromising on quality.",
        date: "Jan 5, 2025",
        readTime: "7 min read",
        category: "Student Guide",
        image: "https://images.pexels.com/photos/5905857/pexels-photo-5905857.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 5,
        title: "MacBook vs Windows: Which Ecosystem is Right for You?",
        excerpt: "A comprehensive comparison of Apple and Windows laptops, helping you choose the ecosystem that best fits your workflow and preferences.",
        date: "Jan 2, 2025",
        readTime: "9 min read",
        category: "Comparison",
        image: "https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=800"
    },
    {
        id: 6,
        title: "Understanding CPU and GPU Benchmarks: A Simple Guide",
        excerpt: "Demystify laptop performance metrics with our easy-to-understand guide on CPU and GPU benchmarks and what they mean for you.",
        date: "Dec 28, 2024",
        readTime: "5 min read",
        category: "Education",
        image: "https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg?auto=compress&cs=tinysrgb&w=800"
    }
];

export const BlogPage: React.FC<{ onNavigate?: (page: 'blog-detail', slug: string) => void }> = ({ onNavigate }) => {
    const [blogPosts, setBlogPosts] = useState<BlogPostType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function fetchBlogPosts() {
            try {
                setIsLoading(true);
                const posts = await getRecentBlogPosts(12);
                setBlogPosts(posts);
            } catch (err) {
                console.error('Error fetching blog posts:', err);
                setError('Failed to load blog posts');
            } finally {
                setIsLoading(false);
            }
        }
        fetchBlogPosts();
    }, []);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getReadTime = (content: string) => {
        const wordsPerMinute = 200;
        const wordCount = content.split(/\s+/).length;
        const minutes = Math.ceil(wordCount / wordsPerMinute);
        return `${minutes} min read`;
    };

    return (
        <div className="min-h-screen bg-[var(--surface-1)]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 animate-fade-in">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <motion.h1 
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[var(--text-1)] font-serif mb-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            Blog
                        </motion.h1>
                        <motion.p 
                            className="text-lg sm:text-xl text-[var(--text-2)] max-w-2xl mx-auto leading-relaxed"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            AI-powered insights, laptop reviews, and tech guides automatically generated from real user searches.
                        </motion.p>
                    </div>

                    {isLoading && (
                        <div className="text-center py-20">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[var(--brand)] border-t-transparent"></div>
                            <p className="mt-4 text-[var(--text-3)]">Loading blog posts...</p>
                        </div>
                    )}

                    {error && (
                        <div className="text-center py-20">
                            <p className="text-[var(--error)]">{error}</p>
                        </div>
                    )}

                    {!isLoading && !error && blogPosts.length === 0 && (
                        <div className="text-center py-20">
                            <p className="text-[var(--text-3)] text-lg">No blog posts yet. Try searching for laptops to generate content!</p>
                        </div>
                    )}

                    {!isLoading && !error && blogPosts.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                            {blogPosts.map((post, index) => (
                                <motion.article
                                    key={post.id}
                                    className="group bg-[var(--surface-2)] rounded-2xl overflow-hidden shadow-lg border border-[var(--surface-4)] hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.05 }}
                                    onClick={() => {
                                        if (onNavigate) {
                                            onNavigate('blog-detail', post.slug);
                                        } else {
                                            window.location.href = `#blog/${post.slug}`;
                                        }
                                    }}
                                >
                                    {/* Cover Image */}
                                    {post.cover_image && (
                                        <div className="relative h-48 overflow-hidden rounded-t-2xl">
                                            <img
                                                src={post.cover_image}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Category Badge */}
                                    <div className="p-6 sm:p-7">
                                        <div className="mb-4">
                                            <span className="inline-block px-3 py-1 text-xs font-semibold text-[var(--brand)] bg-[var(--brand-lighter)] rounded-full">
                                                AI Generated
                                            </span>
                                        </div>

                                        {/* Title */}
                                        <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-1)] mb-3 group-hover:text-[var(--brand)] transition-colors line-clamp-2">
                                            {post.title}
                                        </h2>

                                        {/* Excerpt */}
                                        <p className="text-[var(--text-2)] text-sm sm:text-base leading-relaxed mb-5 line-clamp-3">
                                            {post.excerpt}
                                        </p>

                                        {/* Laptops Mentioned */}
                                        {post.laptops_mentioned && post.laptops_mentioned.length > 0 && (
                                            <div className="mb-4 flex flex-wrap gap-1">
                                                {post.laptops_mentioned.slice(0, 2).map((laptop, idx) => (
                                                    <span key={idx} className="text-xs px-2 py-1 bg-[var(--surface-3)] text-[var(--text-3)] rounded">
                                                        {laptop}
                                                    </span>
                                                ))}
                                                {post.laptops_mentioned.length > 2 && (
                                                    <span className="text-xs px-2 py-1 bg-[var(--surface-3)] text-[var(--text-3)] rounded">
                                                        +{post.laptops_mentioned.length - 2} more
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Meta */}
                                        <div className="flex items-center justify-between text-sm text-[var(--text-3)] pt-4 border-t border-[var(--surface-4)]">
                                            <div className="flex items-center gap-2">
                                                <CalendarIcon />
                                                <span>{formatDate(post.created_at)}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <ClockIcon />
                                                <span>{getReadTime(post.content)}</span>
                                            </div>
                                        </div>

                                        {/* View Count */}
                                        {post.view_count > 0 && (
                                            <div className="mt-2 text-xs text-[var(--text-3)]">
                                                {post.view_count} views
                                            </div>
                                        )}
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
